import React, { useState } from 'react';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { toast } from '../hooks/use-toast';
import { conferenceAPI, couponAPI } from '../services/api';
import { Loader2, CalendarIcon, Tag, CheckCircle2 } from 'lucide-react';
import { format } from 'date-fns';

const categoryOptions = [
  'Technical SEO', 'Content Strategy', 'Analytics & AI',
  'Local SEO', 'Link Building', 'General Search'
];

const SubmitConferenceForm = () => {
  const [formData, setFormData] = useState({
    title: '', organizer: '', speakers: '', description: '',
    organizerEmail: '', date: '', location: '', categories: [],
    priceRange: '', currency: 'USD', format: 'In-Person',
    websiteUrl: '', ticketUrl: '', coverImage: null
  });
  const [loading, setLoading] = useState(false);
  const [paymentUrls, setPaymentUrls] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({ ...prev, coverImage: e.target.files[0] }));
  };

  const toggleCategory = (category) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '', organizer: '', speakers: '', description: '',
      organizerEmail: '', date: '', location: '', categories: [],
      priceRange: '', currency: 'USD', format: 'In-Person',
      websiteUrl: '', ticketUrl: '', coverImage: null
    });
    setPaymentUrls(null);
    setCouponCode('');
    setCouponApplied(null);
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    try {
      const res = await couponAPI.validate(couponCode);
      setCouponApplied(res.data);
      toast({ title: 'Coupon Applied', description: `${res.data.discount}% discount applied! New price: $${res.data.discountedPrice}` });
    } catch (err) {
      setCouponApplied(null);
      toast({ title: 'Invalid Coupon', description: err.response?.data?.detail || 'Coupon code is not valid', variant: 'destructive' });
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponApplied(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = [];
    if (!formData.title) missing.push('Event Title');
    if (!formData.organizer) missing.push('Organizer');
    if (!formData.organizerEmail) missing.push('Contact Email');
    if (!formData.date) missing.push('Event Date');
    if (!formData.location) missing.push('Location');
    if (!formData.speakers) missing.push('Speakers');
    if (!formData.description) missing.push('Description');
    if (formData.categories.length === 0) missing.push('Categories');
    if (!formData.priceRange) missing.push('Price Range');
    if (!formData.websiteUrl) missing.push('Website URL');
    if (!formData.ticketUrl) missing.push('Ticket URL');
    if (!formData.coverImage) missing.push('Cover Image');
    if (missing.length > 0) {
      toast({ title: 'Missing Fields', description: `Please fill in: ${missing.join(', ')}`, variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const submitData = { ...formData };
      if (couponApplied) submitData.couponCode = couponApplied.code;
      const response = await conferenceAPI.submit(submitData);
      const data = response.data;
      // Push event to GTM dataLayer
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'conference_form_submit',
        form_name: 'submit_conference',
        conference_title: formData.title,
        conference_format: formData.format,
      });
      if (data.error) {
        toast({ title: 'Submission Received', description: 'Your conference has been submitted but payment processing is temporarily unavailable.' });
      } else {
        setPaymentUrls({ stripe: data.stripe?.url, paypal: data.paypal?.url, conferenceId: data.conferenceId });
      }
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to submit conference.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  if (paymentUrls) {
    const finalPrice = couponApplied ? couponApplied.discountedPrice : 49;
    return (
      <Card className="p-8" data-testid="payment-card">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Complete Your Submission</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          To prevent spam, we require a one-time processing fee. Choose your payment method below:
        </p>
        {couponApplied ? (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-8 flex items-center justify-between">
            <div>
              <span className="text-sm text-green-700 dark:text-green-400 font-medium">Coupon <strong>{couponApplied.code}</strong> applied ({couponApplied.discount}% off)</span>
            </div>
            <div className="text-right">
              <span className="text-sm line-through text-gray-400 mr-2">$49.00</span>
              <span className="text-2xl font-bold text-green-600">${finalPrice.toFixed(2)}</span>
            </div>
          </div>
        ) : (
          <p className="text-2xl font-bold text-gray-900 dark:text-white mb-8">$49.00 USD</p>
        )}
        <div className="space-y-6">
          <div className="border-2 border-purple-200 dark:border-purple-800 rounded-lg p-6 space-y-4">
            {paymentUrls.stripe && (
              <Button onClick={() => window.location.href = paymentUrls.stripe} className="w-full bg-purple-600 hover:bg-purple-700 text-white h-12 text-lg">
                Pay with Stripe
              </Button>
            )}
            {paymentUrls.paypal && (
              <Button onClick={() => window.location.href = paymentUrls.paypal} className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-lg">
                Pay with PayPal
              </Button>
            )}
          </div>
          <Button variant="outline" onClick={() => setPaymentUrls(null)} className="w-full">Back to Form</Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-8" data-testid="submit-conference-form">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <Label htmlFor="title">Event Title <span className="text-red-500">*</span></Label>
            <Input id="title" name="title" value={formData.title} onChange={handleInputChange} placeholder="e.g., SEO Summit 2026" required />
          </div>
          <div>
            <Label htmlFor="organizer">Organizer <span className="text-red-500">*</span></Label>
            <Input id="organizer" name="organizer" value={formData.organizer} onChange={handleInputChange} placeholder="Company or person name" />
          </div>
          <div>
            <Label htmlFor="organizerEmail">Contact Email <span className="text-red-500">*</span></Label>
            <Input id="organizerEmail" name="organizerEmail" type="email" value={formData.organizerEmail} onChange={handleInputChange} placeholder="contact@example.com" required />
          </div>
          <div>
            <Label>Event Date <span className="text-red-500">*</span></Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="event-date-picker" className={`w-full justify-start text-left font-normal mt-1 ${!formData.date ? 'text-muted-foreground' : ''}`}>
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.date ? format(new Date(formData.date), 'PPP') : 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 z-50" align="start">
                <Calendar mode="single" selected={formData.date ? new Date(formData.date) : undefined} onSelect={(day) => { if (day) setFormData(prev => ({ ...prev, date: format(day, 'yyyy-MM-dd') })); }} initialFocus />
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <Label htmlFor="location">Location <span className="text-red-500">*</span></Label>
            <Input id="location" name="location" value={formData.location} onChange={handleInputChange} placeholder="City, Country" />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="speakers">Speakers (comma separated) <span className="text-red-500">*</span></Label>
            <Input id="speakers" name="speakers" value={formData.speakers} onChange={handleInputChange} placeholder="John Doe, Jane Smith" />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="description">Description <span className="text-red-500">*</span></Label>
            <Textarea id="description" name="description" value={formData.description} onChange={handleInputChange} placeholder="Describe your event..." rows={4} />
          </div>
          <div className="md:col-span-2">
            <Label>Categories <span className="text-red-500">*</span></Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {categoryOptions.map((cat) => (
                <Badge key={cat} variant={formData.categories.includes(cat) ? 'default' : 'outline'} className={`cursor-pointer ${formData.categories.includes(cat) ? 'bg-purple-600 text-white' : 'hover:bg-purple-50 dark:hover:bg-purple-900/20'}`} onClick={() => toggleCategory(cat)}>
                  {cat}
                </Badge>
              ))}
            </div>
          </div>
          <div>
            <Label>Format</Label>
            <Select value={formData.format} onValueChange={(value) => setFormData(prev => ({ ...prev, format: value }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="In-Person">In-Person</SelectItem>
                <SelectItem value="Online">Online</SelectItem>
                <SelectItem value="Hybrid">Hybrid</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Currency</Label>
            <Select value={formData.currency} onValueChange={(value) => setFormData(prev => ({ ...prev, currency: value }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="GBP">GBP</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="priceRange">Price Range <span className="text-red-500">*</span></Label>
            <Input id="priceRange" name="priceRange" value={formData.priceRange} onChange={handleInputChange} placeholder="e.g., $99 - $499 or Free" />
          </div>
          <div>
            <Label htmlFor="websiteUrl">Website URL <span className="text-red-500">*</span></Label>
            <Input id="websiteUrl" name="websiteUrl" value={formData.websiteUrl} onChange={handleInputChange} placeholder="www.example.com" />
          </div>
          <div>
            <Label htmlFor="ticketUrl">Ticket URL <span className="text-red-500">*</span></Label>
            <Input id="ticketUrl" name="ticketUrl" value={formData.ticketUrl} onChange={handleInputChange} placeholder="https://tickets.example.com" />
          </div>
          <div>
            <Label htmlFor="coverImage">Cover Image <span className="text-red-500">*</span></Label>
            <Input id="coverImage" name="coverImage" type="file" onChange={handleFileChange} accept="image/*" />
          </div>
        </div>

        <div className="bg-purple-50 dark:bg-purple-900/20 p-6 rounded-lg space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Submission Fee</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                One-time processing fee to prevent spam. Your event will be reviewed and published within 24-48 hours.
              </p>
            </div>
            <div className="text-right">
              {couponApplied ? (
                <div>
                  <span className="text-sm line-through text-gray-400">$49.00</span>
                  <p className="text-2xl font-bold text-green-600">${couponApplied.discountedPrice}</p>
                  <span className="text-xs text-green-600 font-medium">-{couponApplied.discount}% OFF</span>
                </div>
              ) : (
                <p className="text-2xl font-bold text-gray-900 dark:text-white">$49.00</p>
              )}
            </div>
          </div>
          
          <div className="border-t border-purple-200 dark:border-purple-800 pt-4">
            <Label className="text-sm font-medium mb-2 block">Have a coupon code?</Label>
            {couponApplied ? (
              <div className="flex items-center gap-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                <div className="flex-1">
                  <span className="font-semibold text-green-700 dark:text-green-400">{couponApplied.code}</span>
                  <span className="text-sm text-green-600 dark:text-green-500 ml-2">({couponApplied.discount}% discount applied)</span>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={handleRemoveCoupon} className="text-red-500 hover:text-red-700">
                  Remove
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Enter coupon code"
                    className="pl-10 uppercase"
                    data-testid="coupon-input"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleApplyCoupon}
                  disabled={couponLoading || !couponCode.trim()}
                  data-testid="apply-coupon-btn"
                >
                  {couponLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                </Button>
              </div>
            )}
          </div>
        </div>

        <Button type="submit" disabled={loading} className="w-full bg-purple-600 hover:bg-purple-700 text-white h-12 text-lg" data-testid="submit-conference-btn">
          {loading ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing...</>) : 'Continue to Payment'}
        </Button>
      </form>
    </Card>
  );
};

export default SubmitConferenceForm;
